/*!
 * Stripped fork of https://github.com/Starcounter-Jack/JSON-Patch
 * Only exposes applyPatch + compare + Operation (see index.ts).
 * (c) 2017-2022 Joachim Wester — MIT licensed
 */

// Recursive clone — avoids the serialize→string→parse roundtrip of
// JSON.parse(JSON.stringify()). Pre-allocates arrays for fewer GC cycles.
// Normalizes undefined → null (JSON has no undefined).
export function _deepClone(obj: any): any {
  if (obj === undefined) return null
  if (typeof obj !== 'object' || obj === null) return obj
  if (Array.isArray(obj)) {
    const len = obj.length
    const arr = new Array(len)
    for (let i = 0; i < len; i++) arr[i] = _deepClone(obj[i])
    return arr
  }
  const keys = Object.keys(obj)
  const len = keys.length
  const clone: any = {}
  for (let i = 0; i < len; i++) {
    const key = keys[i]
    clone[key] = _deepClone(obj[key])
  }
  return clone
}

// charCode loop — 3x faster than cached /^\d+$/.test(str)
function isInteger(str: string): boolean {
  let i = 0
  const len = str.length
  let charCode
  while (i < len) {
    charCode = str.charCodeAt(i)
    if (charCode >= 48 && charCode <= 57) {
      i++
      continue
    }
    return false
  }
  return true
}

// RFC 6902 §4: '~' is escaped as '~0', '/' as '~1'. Order matters on decode.
function unescapePathComponent(path: string): string {
  return path.replace(/~1/g, '/').replace(/~0/g, '~')
}

export type Operation =
  | AddOperation<any>
  | RemoveOperation
  | ReplaceOperation<any>
  | MoveOperation
  | CopyOperation
  | TestOperation<any>

type BaseOperation = {
  path: string
}

type AddOperation<T> = BaseOperation & {
  op: 'add'
  value: T
}

type RemoveOperation = BaseOperation & {
  op: 'remove'
}

type ReplaceOperation<T> = BaseOperation & {
  op: 'replace'
  value: T
}

type MoveOperation = BaseOperation & {
  op: 'move'
  from: string
}

type CopyOperation = BaseOperation & {
  op: 'copy'
  from: string
}

type TestOperation<T> = BaseOperation & {
  op: 'test'
  value: T
}

// RFC 6901 JSON Pointer: split on '/', walk segments, unescape only when needed.
function getValueByPointer(document: any, pointer: string): any {
  if (pointer == '') return document
  const keys = pointer.split('/')
  let obj = document
  for (let t = 1; t < keys.length; t++) {
    let key: string = keys[t]
    if (key.indexOf('~') != -1) key = unescapePathComponent(key)
    obj = obj[key]
  }
  return obj
}

// A missing/non-container parent means this patch cannot apply to our state.
// Throw so the subscription can recover with a fresh snapshot.
function container(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    throw new TypeError('JSON-Patch: target parent must be an object or array')
  }
  return obj
}

// Copy-on-write within one applyPatch call, as Immer's applyPatches does by
// running every patch inside one produce and copying a draft only once.
// `owned` holds exactly the shallow copies this call created, each linked at
// one place in the result, so later operations may mutate them in place. A
// container that is not owned (the input, a patch value, or anything they
// share) is copied before the first write. Ownership is tracked per copy, not
// per original: an input subtree reachable from two paths gets an independent
// copy for each path written, so writes never become visible through aliases.
function own(obj: any, owned: WeakSet<object>): any {
  if (owned.has(obj)) return obj
  obj = container(obj)
  let copy: any
  if (Array.isArray(obj)) copy = obj.slice()
  else copy = { ...obj }
  owned.add(copy)
  return copy
}

// Walks the JSON pointer to the parent, then dispatches via switch.
// Inlined ops (no dispatch table / .call()) for better JIT optimization.
//
// Path-copying: every container on the way from the root to the target is
// owned (copied at most once per batch) and relinked, and the operation mutates
// only owned copies. The input document and patch values are never mutated,
// and everything off the written paths keeps its identity, so a consumer
// comparing by reference sees exactly the subtrees the patch touched as new.
function applyOperation<T>(document: T, operation: Operation, owned: WeakSet<object>): T {
  // Root-path operations replace the entire document.
  if (operation.path === '') {
    if (operation.op === 'add' || operation.op === 'replace') {
      return operation.value
    } else if (operation.op === 'move' || operation.op === 'copy') {
      return getValueByPointer(document, (operation as any).from)
    } else if (operation.op === 'test') {
      if (!_areEquals(document, operation.value)) {
        throw new Error('Test operation failed')
      }
      return document
    } else if (operation.op === 'remove') {
      return null as any
    } else {
      return document
    }
  }

  // RFC 6902 §4.4–4.5: move removes then adds; copy adds an independent clone so
  // later operations cannot write through an alias of its source.
  if (operation.op === 'move') {
    const val = getValueByPointer(document, operation.from)
    const removed = applyOperation(document, { op: 'remove', path: operation.from }, owned)
    return applyOperation(removed, { op: 'add', path: operation.path, value: val }, owned)
  }
  if (operation.op === 'copy') {
    const val = _deepClone(getValueByPointer(document, operation.from))
    return applyOperation(document, { op: 'add', path: operation.path, value: val }, owned)
  }

  // Walk to the parent of the target key. Writes own each container on the way;
  // a test only reads, so it neither copies nor relinks anything.
  const write = operation.op !== 'test'
  const keys = operation.path.split('/')
  let root: any
  if (write) root = own(document, owned)
  else root = container(document)
  let obj: any = root
  let t = 1
  const len = keys.length
  let key: any

  while (true) {
    key = keys[t]
    if (key && key.indexOf('~') != -1) {
      key = unescapePathComponent(key)
    }
    // Prototype pollution guard.
    if (
      key == '__proto__' ||
      (key == 'prototype' && t > 0 && keys[t - 1] == 'constructor')
    ) {
      throw new TypeError(
        'JSON-Patch: modifying `__proto__` or `constructor/prototype` prop is banned for security reasons',
      )
    }
    t++
    const isArr = Array.isArray(obj)
    if (isArr) {
      if (key === '-') key = obj.length // RFC 6901: '-' = past-the-end index
      else if (isInteger(key)) key = ~~key
    }
    // Reached the parent — apply the operation on obj[key].
    if (t >= len) {
      switch (operation.op) {
        case 'add':
          if (isArr && typeof key == 'number') {
            obj.splice(key, 0, operation.value)
          } else {
            obj[key] = operation.value
          }
          return root
        case 'remove':
          if (isArr) {
            obj.splice(key, 1)
          } else {
            delete obj[key]
          }
          return root
        case 'replace':
          obj[key] = operation.value
          return root
        case 'test':
          if (!_areEquals(obj[key], operation.value)) {
            throw new Error('Test operation failed')
          }
          return document
        default:
          return document
      }
    }
    if (write) obj[key] = own(obj[key], owned)
    obj = container(obj[key])
  }
}

/**
 * Apply an RFC 6902 patch array. Never mutates the input or patch values:
 * each container on a written path is copied once per call and then reused by
 * later operations; everything else keeps its identity.
 */
export function applyPatch<T>(document: T, patch: ReadonlyArray<Operation>): T {
  const owned = new WeakSet<object>()
  for (let i = 0, length = patch.length; i < length; i++) {
    document = applyOperation(document, patch[i], owned)
  }
  return document
}

// Adapted from https://github.com/epoberezkin/fast-deep-equal (MIT).
// Single pass over object keys: hasOwn guard short-circuits before recursion.
export function _areEquals(a: any, b: any): boolean {
  if (a === b) return true
  if (a == null && b == null) return true // null ≡ undefined
  if (a && b && typeof a == 'object' && typeof b == 'object') {
    const arrA = Array.isArray(a)
    const arrB = Array.isArray(b)
    let i: number
    let length: number

    if (arrA && arrB) {
      length = a.length
      if (length != b.length) return false
      for (i = length; i-- !== 0; ) if (!_areEquals(a[i], b[i])) return false
      return true
    }
    if (arrA != arrB) return false
    const keys = Object.keys(a)
    length = keys.length
    if (length !== Object.keys(b).length) return false
    for (i = length; i-- !== 0; ) {
      const key = keys[i]
      if (!Object.hasOwn(b, key) || !_areEquals(a[key], b[key])) return false
    }
    return true
  }
  return a !== a && b !== b // NaN === NaN
}
